import { Button, useModalState } from '@sequencehq/core-components'
import { IntegrationServices } from '@sequencehq/api/utils'
import { FC } from 'react'
import { LinkOrCreateCustomerModal } from './LinkOrCreateCustomerModal'
import { LinkSalesforceCustomerModal } from 'Integrations/integrationsConfig/salesforce/InspectorItem/LinkSalesforceCustomerModal'

/**
 * We have different modals for linking customers for different services. This
 * map ensures we will load in the correct one! If a component isn't found
 * here, then we expect to not show the 'Resolve' button.
 */
const customerLinkModalMap: Partial<{
  [service in IntegrationServices]: FC<{
    customerId: string
    onClose: () => void
  }>
}> = {
  Xero: ({ customerId, onClose }) => (
    <LinkOrCreateCustomerModal
      service="Xero"
      customerId={customerId}
      onClose={onClose}
    />
  ),
  Salesforce: ({ customerId, onClose }) => (
    <LinkSalesforceCustomerModal customerId={customerId} onClose={onClose} />
  ),
  QuickBooks_Online: ({ customerId, onClose }) => (
    <LinkOrCreateCustomerModal
      service="QuickBooks_Online"
      customerId={customerId}
      onClose={onClose}
    />
  ),
  Rillet: ({ customerId, onClose }) => (
    <LinkOrCreateCustomerModal
      service="Rillet"
      customerId={customerId}
      onClose={onClose}
    />
  )
}

/**
 * A 'Resolve' button that opens the appropriate link-customer modal for the
 * given service, used by the invoice/credit-note integration warning banners.
 * Renders nothing when the service has no link modal.
 */
export const ResolveCustomerLinkButton: FC<{
  service: IntegrationServices
  customerId: string
}> = ({ service, customerId }) => {
  const { isModalOpen, openModal, closeModal } = useModalState()
  const ModalComponent = customerLinkModalMap[service]

  if (!ModalComponent) {
    return null
  }

  return (
    <>
      <Button
        variant="secondary"
        onClick={openModal}
        data-testid="integrationWarnings.resolve"
      >
        Resolve
      </Button>
      {isModalOpen && (
        <ModalComponent customerId={customerId} onClose={closeModal} />
      )}
    </>
  )
}
